import { useSubmitLoopFieldDesignDocumentsMutation } from '@/services/loopFieldPackage';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { useCreateSystemDesignMutation } from '@/services/systemDesign';
import { ReviewPackageActions } from '@/types/ReviewPackage';
import {
  Alert,
  Box,
  BoxProps,
  Button,
  FormikSelectInputNative,
  FormikTextInput,
  Modal,
  toast,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  DocumentTypes,
  GeothermalDesignDocumentTypes,
  GeothermalEquipmentTypeLabels,
  GeothermalManufacturer,
  ProgramType,
  QueueStatus,
  QueueType,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetDocumentsQuery } from '../../services/documents';
import { BaseRejectionReasons } from '../../types/BasePackageTypes';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';
import PageHeader from '../PageHeader';

import { DesignDocumentsReview } from './DesignDocumentsReview';
import {
  buildInitialValues,
  buildSystemDesignPayload,
  buildSystemDetails,
  DesignDocsFormValues,
  hasCompleteSystemDetails,
  hasEnteredSystemDetails,
} from './loopFieldSystemDetails';

const designDocFilter = Object.values(GeothermalDesignDocumentTypes);

const designDocFilterForQuery = designDocFilter as unknown as DocumentTypes[];

// Lock uploads only once QC has claimed the queue or finalized it.
const lockingStatuses = new Set<QueueStatus>([
  QueueStatus.inProgress,
  QueueStatus.reReview,
  QueueStatus.approved,
  QueueStatus.conditionallyApproved,
]);

const manufacturerOptions = [
  { label: 'Select manufacturer...', value: '' },
  ...Object.values(GeothermalManufacturer).map((manufacturer) => ({ label: manufacturer, value: manufacturer })),
];

const DesignDocsSystemDetailsSchema = yup.object().shape({
  systems: yup
    .array()
    .of(
      yup.object().shape({
        equipment: yup
          .array()
          .of(yup.object().shape({ model: yup.string().trim().required('Model is required.') }))
          .min(1, 'At least one equipment entry is required.'),
      }),
    )
    .min(1, 'At least one system is required.'),
});

const footerBoxStyle: BoxProps = {
  direction: { base: 'column', tablet: 'row' },
  alignItems: { base: 'stretch', tablet: 'flex-end' },
  justifyContent: { tablet: 'flex-end' },
  childGap: 'sm',
  style: { flexShrink: 0 },
  position: 'relative',
  padding: 'lg 0 0 0',
};

const footerBoxWidth = { base: '100' as const, tablet: 'auto' as const };

const fieldRowDirection: BoxProps['direction'] = { base: 'column', tablet: 'row' };

const noopSubmit = () => {
  // Submission runs from the confirmation modal, not a native form submit.
};

export const LoopFieldDesignDocSubmission = () => {
  const { id = '', action } = useParams<{ id: string; action: ReviewPackageActions }>();
  const navigate = useNavigate();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const isReviewMode = action === ReviewPackageActions.review;

  const { data: account } = useGetAccountQuery(id, { skip: !id });
  const { data: designDocsQueue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery(
    { accountId: id, type: QueueType.loopFieldDesignDocuments },
    { skip: !id },
  );
  const { data: designDocs } = useGetDocumentsQuery(
    { accountId: id, documentTypeFilter: designDocFilterForQuery },
    { skip: !id },
  );
  const [submitDesignDocs, { isLoading: isSubmitting }] = useSubmitLoopFieldDesignDocumentsMutation();
  const [createSystemDesign, { isLoading: isSaving }] = useCreateSystemDesignMutation();
  const { isOpen: isConfirmOpen, handleOpen: openConfirm, handleClose: closeConfirm } = useOpenClose();

  const queueStatus = designDocsQueue?.status as QueueStatus | undefined;
  const hasDesignDocs = !!designDocs?.length;
  const isRejected = queueStatus === QueueStatus.rejected;
  const isApproved = queueStatus === QueueStatus.approved;
  const uploadIsLocked = !isFinCoUser && !!queueStatus && lockingStatuses.has(queueStatus);
  const systemDetailsAreLocked = uploadIsLocked || isReviewMode;

  const primaryApplicantName = account?.primaryApplicantName;

  const initialValues = useMemo<DesignDocsFormValues>(
    () => buildInitialValues(account?.systemDesign?.systems ?? []),
    [account?.systemDesign?.systems],
  );

  const latestRejection = useMemo(() => {
    if (!isRejected || !designDocsQueue?.rejectionReasons?.length) return undefined;
    return [...designDocsQueue.rejectionReasons].toSorted(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    )[0];
  }, [isRejected, designDocsQueue?.rejectionReasons]);
  const showRejectionAlert = !isFinCoUser && !!latestRejection;

  const primaryButtonLabel = useMemo(() => {
    if (!hasDesignDocs) return 'Skip';
    if (isRejected) return 'Resubmit for Review';
    return 'Submit for Review';
  }, [hasDesignDocs, isRejected]);

  const skipToLoopField = useCallback(() => {
    navigate(`/accounts/${id}/loop-field-package/create`);
  }, [navigate, id]);

  const goBack = useCallback(() => {
    navigate(`/accounts/${id}`);
  }, [navigate, id]);

  // Skipping still persists whatever was typed. Submit is the only other write path and it requires
  // a document, so without this the details would be dropped on navigation.
  const handlePrimaryClick = useCallback(
    async (values: DesignDocsFormValues) => {
      if (hasDesignDocs) {
        openConfirm();
        return;
      }
      if (hasEnteredSystemDetails(values)) {
        try {
          await createSystemDesign({
            accountId: id,
            programType: ProgramType.geothermal,
            systemDesign: buildSystemDesignPayload(account?.systemDesign, values),
          }).unwrap();
        } catch (e: any) {
          // Stay put rather than navigating away from unsaved work.
          toast.error(e?.data?.message || 'Error saving system details');
          return;
        }
      }
      skipToLoopField();
    },
    [hasDesignDocs, openConfirm, skipToLoopField, createSystemDesign, account?.systemDesign, id],
  );

  const handleConfirmSubmit = useCallback(
    async (values: DesignDocsFormValues) => {
      try {
        await submitDesignDocs({ accountId: id, systemDetails: buildSystemDetails(values) }).unwrap();
        toast.success('Design documents submitted for review');
        closeConfirm();
        skipToLoopField();
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error submitting design documents');
      }
    },
    [submitDesignDocs, id, closeConfirm, skipToLoopField],
  );

  if (!id) return null;

  return (
    <Formik<DesignDocsFormValues>
      initialValues={initialValues}
      validationSchema={DesignDocsSystemDetailsSchema}
      enableReinitialize
      onSubmit={noopSubmit}
    >
      {({ values }) => {
        // Derived rather than Formik's `isValid`, which starts true on an untouched form: nothing
        // here submits through Formik, so validation may never have run when the gate is read.
        const detailsAreComplete = hasCompleteSystemDetails(values);

        return (
          <>
            <PageHeader
              eyebrow={<Link to={`/accounts/${id}`}>{primaryApplicantName}</Link>}
              title={isReviewMode ? 'Review Design Documents' : 'Submit Design Documents'}
            />
            <MainContainer>
              <Box childGap="md">
                {showRejectionAlert && (
                  <Alert
                    variant="danger"
                    hasIcon
                    title="Design Documents Rejected"
                    message={
                      <Box childGap="xs">
                        {!!latestRejection?.reasons?.length && (
                          <Box fontWeight="medium">
                            {latestRejection.reasons
                              .map(
                                (reason: string) =>
                                  BaseRejectionReasons[reason as keyof typeof BaseRejectionReasons] ?? reason,
                              )
                              .join(', ')}
                          </Box>
                        )}
                        {latestRejection?.note && <Box>{latestRejection.note}</Box>}
                      </Box>
                    }
                  />
                )}
                <FormSection
                  title="Design Documents"
                  description="Load calculation and equipment selection (Manual J & S). Loop Design report from approved tool."
                >
                  <DesignDocumentsReview />
                  <DocumentList
                    documentTypeFilter={designDocFilter}
                    title="Design Documents"
                    showTypeLabelOnItems
                    showDocumentActions={!uploadIsLocked}
                    showUploadButton={!uploadIsLocked}
                    baseCategory="designDocuments"
                  />
                </FormSection>
                <FormSection
                  title="System Details"
                  description="Model for each piece of equipment, so the design can be validated before install. Serial numbers are captured at Install."
                >
                  <FieldArray name="systems">
                    {() => (
                      <Box gap="lg">
                        {values.systems.map((system, index) => (
                          <Box key={`system-${index}`} padding="lg" background="secondary" radius="md" gap="lg">
                            <Box fontSize="lg" fontWeight="medium">
                              {system.name}
                            </Box>
                            <Box flex="auto" minWidth="0">
                              <Field
                                label="Manufacturer"
                                name={`systems.${index}.manufacturer`}
                                id={`systems.${index}.manufacturer`}
                                component={FormikSelectInputNative}
                                options={manufacturerOptions}
                                isDisabled={true}
                                helpText="Set on the system design. Changing it requires voiding the contract and re-quoting."
                              />
                            </Box>
                            {system.equipment.map((item, equipmentIndex) => (
                              <Box key={item.type} gap="lg">
                                {system.equipment.length > 1 && (
                                  <Box fontWeight="medium" color="body-secondary">
                                    {GeothermalEquipmentTypeLabels[item.type]}
                                  </Box>
                                )}
                                <Box direction={fieldRowDirection} gap="lg">
                                  <Box flex="auto" minWidth="0">
                                    <Field
                                      label="Model"
                                      name={`systems.${index}.equipment.${equipmentIndex}.model`}
                                      id={`systems.${index}.equipment.${equipmentIndex}.model`}
                                      component={FormikTextInput}
                                      isDisabled={systemDetailsAreLocked || isSubmitting}
                                    />
                                  </Box>
                                  <Box flex="auto" minWidth="0">
                                    <Field
                                      label="Description (optional)"
                                      name={`systems.${index}.equipment.${equipmentIndex}.description`}
                                      id={`systems.${index}.equipment.${equipmentIndex}.description`}
                                      component={FormikTextInput}
                                      isDisabled={systemDetailsAreLocked || isSubmitting}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ))}
                        {!values.systems.length && (
                          <Box color="body-secondary">No systems are on this account&apos;s system design yet.</Box>
                        )}
                      </Box>
                    )}
                  </FieldArray>
                </FormSection>
                <Box {...footerBoxStyle} width={footerBoxWidth}>
                  <Button onClick={goBack} variant="secondary" tone="neutral">
                    Cancel
                  </Button>
                  {!isReviewMode && (
                    <PrimaryActionButton
                      label={primaryButtonLabel}
                      onClick={handlePrimaryClick}
                      values={values}
                      isLoading={isSubmitting || isSaving}
                      isDisabled={
                        isQueueLoading || (isApproved && !isFinCoUser) || (hasDesignDocs && !detailsAreComplete)
                      }
                    />
                  )}
                  {!isReviewMode && hasDesignDocs && !detailsAreComplete && (
                    <Box color="body-secondary" fontSize="sm">
                      {values.systems.length
                        ? 'Enter a model for every piece of equipment to submit.'
                        : 'A system design is required before design documents can be submitted.'}
                    </Box>
                  )}
                </Box>
              </Box>
            </MainContainer>
            <Modal
              isOpen={isConfirmOpen}
              onDismiss={closeConfirm}
              maxWidth="4xl"
              ariaLabelledBy="confirmDesignDocsSubmission"
            >
              <Modal.Header
                id="confirmDesignDocsSubmission"
                title="Submit Design Documents for QC Review?"
                onDismiss={closeConfirm}
              />
              <Modal.Body background="secondary" childGap="lg">
                <Box fontSize="sm" color="body-primary">
                  Are you ready to submit your Design Documents for review?
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <ConfirmSubmissionFooter
                  isSubmitting={isSubmitting}
                  onCancel={closeConfirm}
                  onConfirm={handleConfirmSubmit}
                  values={values}
                />
              </Modal.Footer>
            </Modal>
          </>
        );
      }}
    </Formik>
  );
};

interface PrimaryActionButtonProps {
  label: string;
  onClick: (values: DesignDocsFormValues) => void;
  values: DesignDocsFormValues;
  isLoading: boolean;
  isDisabled: boolean;
}

/** Binds the current form values to the primary action without an inline arrow in a JSX prop. */
const PrimaryActionButton = ({ label, onClick, values, isLoading, isDisabled }: PrimaryActionButtonProps) => {
  const handleClick = useCallback(() => {
    onClick(values);
  }, [onClick, values]);

  return (
    <Button
      variant="primary"
      onClick={handleClick}
      isLoading={isLoading}
      isDisabled={isDisabled}
      iconSuffix="arrow-right"
    >
      {label}
    </Button>
  );
};

interface ConfirmSubmissionFooterProps {
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (values: DesignDocsFormValues) => void;
  values: DesignDocsFormValues;
}

/** Binds the current form values to the confirm handler without an inline arrow in a JSX prop. */
const ConfirmSubmissionFooter = ({ isSubmitting, onCancel, onConfirm, values }: ConfirmSubmissionFooterProps) => {
  const handleConfirm = useCallback(() => {
    onConfirm(values);
  }, [onConfirm, values]);

  return (
    <Box justifyContent="space-between" display="flex" direction="row" width="100">
      <Button variant="secondary" tone="neutral" disabled={isSubmitting} onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" isLoading={isSubmitting} onClick={handleConfirm}>
        Confirm Submission
      </Button>
    </Box>
  );
};
